import { ChangeDetectorRef, Inject, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { environment } from '@env/environment';
import { IGuestsResponse, MsGuestsService } from '@upr-web-primer/ms-guests';
import { LocalStorageService } from '@upr-web-primer/storage';
import { filter, forEach, map, orderBy } from '@upr-web-primer/udx-utils';
import { WINDOW } from '@upr-web-primer/window';
import { CookieService } from 'ngx-cookie-service';

/**
 * BaseViewEntitlementComponent - Shared logic for view-entitlement components
 * Extracts duplicated methods: loadUserInfo, ddlEvents, goToPage, legacyLink,
 * redirectToWTS, redirectTolinkticket, viewPastTickets, loadInitialTab,
 * getTravelParty, sortEntitlements, filterGuestTickets, getFormattedFullName,
 * mapMembersToNameIds, updateQrCodeText, getLegacyDesc, isDateValid,
 * isLessDateValid, getExpiredTicket, sanitizeInput, orderProfileUrl,
 * getProductName, updateEntitlement, getTickets, sortByDate
 */
export abstract class BaseViewEntitlementComponent {
  entitlementArray: any;
  isEmptyEntitlement = false;
  isShowEntitlement = false;
  pageLabels: any;
  userProfileData: any;
  userData: any;
  fullName: any;
  travelMembersDetails: any[];
  tabData: any = [];
  guestTicketsArray: any[];
  travelPartyTicketsArray: any[];
  travelMembersIds: any[];
  travelIdNames: any[];
  ticketDetailArray: any = [];
  selectedTabIndex: number;
  dateEntry: any;
  legacyDescription: any;
  showTabs = false;
  expiredTicketsArray: any[];
  travelPartyId: any;
  qrCodeText: string;
  displayFirstNameFirst = false;
  viewEntitlement: any;
  removedCount = 0;
  showTicketContent = false;
  errorData: any;

  constructor(
    protected injector: Injector,
    protected commonUtilityService: CommonUtilityService,
    protected guestsService: MsGuestsService,
    protected cdRef: ChangeDetectorRef,
    protected title: Title,
    protected localStorage: LocalStorageService,
    @Inject('AppRoutes') public appRoutes: any,
    @Inject('GlobalDefaults') public globalDefault: any,
    @Inject('AppDefaults') public appDefaults: any,
    @Inject(WINDOW) protected window: any,
    protected cookieService: CookieService,
  ) {}

  /**
   * loadUserInfo - It will load the user data
   */
  loadUserInfo(): any {
    this.guestsService.profile
      .getByGuestId(this.guestsService.helper.getGuestId())
      .then((userData: IGuestsResponse) => {
        this.userProfileData = userData?.personal_info?.contacts;
      })
      .catch((errors: any) => {
        if (errors.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.setUser();
          localStorage.clear();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', environment.cookieSubDomain);
          const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
          this.commonUtilityService.redirectToOidcAppEndSession(redirectUrl);
        }
      });
  }

  /**
   * DDL Events
   */
  ddlEvents(label: any, url: any = ''): void {
    this.commonUtilityService.invokeFireEvent(
      label,
      'Button',
      'Click',
      '',
      new Date().toISOString(),
      'Body | CTA: ' + label,
      url,
      this.window?.digitalData?.page?.category?.primaryCategory ?? '',
      this.window?.digitalData?.page?.category?.subCategory1 ?? '',
    );
  }

  /**
   * goToPage - It will navigate to dashboard component
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  goToPage(type: any) {
    if (type === this.pageLabels?.['page.dashboard']) {
      this.commonUtilityService.changeRoute(this.appRoutes.DASHBOARD);
    }
  }

  /**
   * legacyLink - It will redirect to legacy Url
   */
  legacyLink(): void {
    window.open(
      this.pageLabels?.['linkticket.legacyOrderLink'],
      this.appDefaults.BLANK,
    );
  }

  /**
   * redirectToWTS - Navigate back to WTS account
   */
  redirectToWTS(): any {
    this.commonUtilityService.onClickRedirectWTS();
  }

  /**
   * redirectTolinkticket - Navigate to link tickets
   */
  redirectTolinkticket(): any {
    this.commonUtilityService.setLinkRoute(this.appRoutes.VIEW_ENTITLMENT);
    this.commonUtilityService.changeRoute(this.appRoutes.LINK_TICKETS);
  }

  /**
   * viewPastTickets - Navigate back to past tickets
   */
  viewPastTickets(): void {
    this.commonUtilityService.changeRoute(this.appRoutes.PAST_TICKETS);
    sessionStorage.setItem('wallet', 'true');
  }

  /**
   * updateEmptyEntitlementState - Update empty/show entitlement flags based on array length
   */
  protected updateEmptyEntitlementState(): void {
    if (!this.guestTicketsArray?.length) {
      this.isEmptyEntitlement = true;
      this.isShowEntitlement = false;
    } else {
      this.isEmptyEntitlement = false;
      this.isShowEntitlement = true;
    }
  }

  /**
   * buildTravelPartyTabs - Build tab data from travel members
   */
  protected buildTravelPartyTabs(
    namesWithIds: { id: any; full_name: string }[],
  ): void {
    forEach(namesWithIds, (item: any, index: string | number) => {
      this.tabData.push({
        heading: item?.full_name,
        permaLink: item?.id,
        analyticsTitle:
          this.pageLabels?.['viewEntPopulated.travelPartyLabel'] +
          ' ' +
          ((index as number) + 1),
      });
    });
  }

  /**
   * getLegacyDesc - It will display legacy Desc & legacy CTA
   */
  getLegacyDesc(): void {
    const legacyDesc = this.pageLabels?.['viewEnt.legacyDesc'];
    this.legacyDescription = legacyDesc?.replace(
      this.pageLabels?.['legacyCTA.oldSystem'],
      '',
    );
    this.legacyDescription = this.legacyDescription?.replace(/\./g, '');
  }

  /**
   * isDateValid - compare the ticket date with current Date
   */
  isDateValid(validDate: string): boolean {
    return this.compareDateWithToday(validDate, 'greaterOrEqual');
  }

  /**
   * isLessDateValid - compare the ticket date with current Date
   */
  isLessDateValid(validDate: string): boolean {
    return this.compareDateWithToday(validDate, 'less');
  }

  /**
   * compareDateWithToday - Compares a date string with today's date
   * @param validDate - Date string in "yyyy-MM-dd" format
   * @param comparison - 'greaterOrEqual' returns true if today <= validDate, 'less' returns true if validDate < today
   */
  protected compareDateWithToday(
    validDate: string,
    comparison: 'greaterOrEqual' | 'less',
  ): boolean {
    const currentDate = new Date().toISOString().split('T')[0];
    if (comparison === 'greaterOrEqual') {
      return currentDate <= validDate;
    }
    return validDate < currentDate;
  }

  /**
   * sanitizeInput - Sanitizes the input to prevent XSS attacks by escaping any HTML content.
   */
  sanitizeInput(input: any): string {
    // Convert input to string to avoid unexpected input types
    let sanitizedString = String(input);

    // Create a map of characters that need to be replaced to avoid XSS attacks
    const charMap: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    // Replace only the characters in the charMap with their safe equivalents
    for (const char in charMap) {
      if (charMap.hasOwnProperty(char)) {
        sanitizedString = sanitizedString.replace(
          new RegExp(char, 'g'),
          charMap[char],
        );
      }
    }

    return sanitizedString;
  }

  /**
   * orderProfileUrl - redirecting to the actual page when we right click and open with new tabs and windows.
   */
  orderProfileUrl(endpointName: any): any {
    return this.commonUtilityService.getInternalUrl(`/${endpointName}`);
  }

  /**
   * getProductName - Logic to determine the heading based on categoryCode
   */
  getProductName(data: any): string {
    if (
      data?.categoryCode === this.globalDefault?.COMM_EXPRESS_PASS &&
      data?.expressName
    ) {
      return data?.expressName;
    } else {
      return data?.product_name ?? data?.tridionContentProductName;
    }
  }

  /**
   * updateEntitlement - Refreshes entitlement data and runs callback
   */
  updateEntitlement(callback?: () => void): void {
    const guestId = this.guestsService.helper.getGuestId();
    this.commonUtilityService
      ?.getEntitlements(guestId)
      ?.subscribe((res: any) => {
        this.commonUtilityService?.setResolvedData(res);
        //  Only run after entitlement is updated
        if (callback) callback();
      });
  }

  /**
   * filterGuestTickets - Filters entitlement array for a specific guest based on viewEntitlement flag
   */
  filterGuestTickets(guestId: string): any[] {
    if (this.viewEntitlement) {
      return filter(this.entitlementArray, (item: any) => {
        if (item.guest_id === guestId) {
          if (
            item.hasOwnProperty('valid_to') &&
            this.isDateValid(item.valid_to)
          ) {
            return item;
          }
          if (!item.hasOwnProperty('valid_to')) {
            return item;
          }
        }
      });
    } else {
      return filter(
        this.entitlementArray,
        (item: any) =>
          item.guest_id === guestId && this.isDateValid(item.valid_to),
      );
    }
  }

  /**
   * getFormattedFullName - Returns formatted full name based on displayFirstNameFirst flag
   */
  getFormattedFullName(
    firstName: string | null,
    lastName: string | null,
  ): string {
    if (this.displayFirstNameFirst) {
      return `${firstName ?? ''}  ${lastName ?? ''}`.trim();
    }
    return `${lastName ?? ''}  ${firstName ?? ''}`.trim();
  }

  /**
   * mapMembersToNameIds - Maps travel member details to {id, full_name} array
   */
  mapMembersToNameIds(members: any[]): { id: any; full_name: string }[] {
    return map(members, (item: any) => ({
      id: item?.member_id,
      full_name: this.displayFirstNameFirst
        ? `${item?.contact?.first} ${item?.contact?.last}`
        : `${item?.contact?.last ?? ''} ${item?.contact?.first ?? ''}`.trim(),
    }));
  }

  /**
   * updateQrCodeText - Updates the QR code text description from guest tickets
   */
  updateQrCodeText(): void {
    const productNames: string[] = [];
    if (this.guestTicketsArray?.length) {
      forEach(this.guestTicketsArray, (item: any) => {
        productNames.push(item.product_name);
      });
      this.qrCodeText = `${this.pageLabels?.['dashboard.qrCodeDescription1']} ${productNames} ${this.pageLabels?.['dashboard.qrCodeDescription3']} ${this.fullName}, ${this.pageLabels?.['dashboard.qrCodeDescription2']}`;
    }
  }

  /**
   * getExpiredTicket - To get all expired Tickets
   */
  getExpiredTicket(): void {
    const namesWithIds = this.mapMembersToNameIds(this.travelMembersDetails);

    namesWithIds.push({
      id: this.guestsService.helper.getGuestId(),
      full_name: this.getFormattedFullName(
        this.localStorage.getItem('USER_FNAME'),
        this.localStorage.getItem('USER_LNAME'),
      ),
    });
    this.expiredTicketsArray = namesWithIds
      .map((nameItem) => {
        const matchingTickets = this.expiredTicketsArray?.filter(
          (ticketItem) => ticketItem?.guest_id === nameItem?.id,
        );

        if (matchingTickets?.length > 0) {
          return {
            full_name: nameItem.full_name,
            id: nameItem.id,
            tickets: matchingTickets.map((ticket) => ({
              ticket_id: ticket?.ticket_id,
              valid_from: ticket?.valid_from,
              valid_to: ticket?.valid_from,
              ticket_status: ticket?.ticket_status,
            })),
          };
        }

        return null;
      })
      .filter(Boolean);
    this.expiredTicketsArray.reverse();
  }

  // ─── Abstract hooks for child-specific behavior ───────────────────────

  /**
   * getTicketIdFromData - Returns the ticket identifier from a ticket data object.
   * Override in children: uhulv-ukr uses data.ticket_id, ush uses data.id.
   */
  protected abstract getTicketIdFromData(data: any): string;

  /**
   * getTicketGuestId - Returns the guest ID from an entitlement item.
   * Override in children: uhulv-ukr uses item.guest_id, ush uses item.guest_details?.assigned_guest_id.
   */
  protected abstract getTicketGuestId(item: any): string;

  /**
   * getDateSortField - Returns the field path used for date-based sorting.
   * Override in children: uhulv-ukr uses 'valid_from', ush uses 'validity.valid_from_datetime'.
   */
  protected abstract getDateSortField(): string;

  /**
   * getDateSortValue - Returns the date value from an item for sorting purposes.
   * Override in children to extract the correct date field.
   */
  protected abstract getDateSortValue(item: any): string;

  // ─── Shared concrete methods that use abstract hooks ──────────────────

  /**
   * getTickets - It will navigate to ticket-detail component
   */
  getTickets(data: any): any {
    sessionStorage.removeItem('pastTicketsPage');
    const ticketId = this.getTicketIdFromData(data);
    this.commonUtilityService.changeRoute(
      `${this.appRoutes.TICKET_DETAILS}/${ticketId}`,
    );
    this.ddlEvents(ticketId);
  }

  /**
   * sortByDate - It will sort the content in ascending order as per valid_from
   */
  sortByDate(): void {
    this.guestTicketsArray = orderBy(
      this.guestTicketsArray,
      [this.getDateSortField()],
      ['asc'],
    );
  }

  /**
   * sortEntitlements - sort the entitlements by walletEntitlementSorting then by date
   */
  sortEntitlements(): void {
    this.guestTicketsArray.sort((a: any, b: any) => {
      const orderA = a.walletEntitlementSorting;
      const orderB = b.walletEntitlementSorting;
      if (orderA < orderB) {
        return -1;
      }
      if (orderA > orderB) {
        return 1;
      }

      return (
        new Date(this.getDateSortValue(a)).getTime() -
        new Date(this.getDateSortValue(b)).getTime()
      );
    });
  }

  /**
   * loadInitialTab - It will load the initial Tab.
   * Builds fullName and pushes the logged-in user's tab.
   */
  loadInitialTab(): void {
    this.fullName = this.getFormattedFullName(
      this.localStorage.getItem('USER_FNAME'),
      this.localStorage.getItem('USER_LNAME'),
    );
    const obj = {
      heading: this.fullName,
      permaLink: this.getInitialTabPermaLink(),
      analyticsTitle: this.pageLabels?.['viewEntPopulated.profileGuestLabel'],
    };
    this.tabData.push(obj);
  }

  /**
   * getInitialTabPermaLink - Returns the permaLink ID for the initial tab.
   * Default uses guestsService.helper.getGuestId(). uhulv-ukr overrides to use localStorage.
   */
  protected getInitialTabPermaLink(): string {
    return this.guestsService.helper.getGuestId();
  }

  /**
   * initWalletData - Shared ngOnInit wallet data initialization
   */
  protected initWalletData(): void {
    this.displayFirstNameFirst = environment?.config?.displayFirstNameFirst;
    const storedWalletData = JSON.parse(
      this.localStorage.getItem('walletData') ?? '[]',
    );
    this.commonUtilityService.setWalletResolvedData(storedWalletData);
  }

  /**
   * initEntitlementLoading - Shared entitlement loading check pattern.
   * Subscribes to getEntitlement() and calls dataResolver.checkBaseCode() if needed.
   * @param dataResolver - The DataResolver instance (uhulv-ukr or ush)
   * @param updateLogicCallback - The callback to run after entitlements load
   */
  protected initEntitlementLoading(
    dataResolver: { checkBaseCode: () => any },
    updateLogicCallback: () => void,
  ): void {
    if (!sessionStorage.getItem('entitlementLoaded')) {
      this.commonUtilityService?.getEntitlement()?.subscribe((val: any) => {
        if (val === false) {
          dataResolver.checkBaseCode().subscribe(() => {
            sessionStorage.setItem('entitlementLoaded', 'true');
            updateLogicCallback();
          });
        }
      });
    }
  }

  /**
   * initCommonEntitlementSetup - Shared setup after entitlement data is loaded
   */
  protected initCommonEntitlementSetup(): void {
    this.travelMembersDetails =
      this.commonUtilityService.getTravelMembersDetails();
    this.title.setTitle(this.pageLabels?.['page.walletDetail']);
    this.dateEntry = this.pageLabels?.['viewEntPopulated.entryDate'];
    this.loadInitialTab();
    this.selectedTabIndex = 0;
  }

  /**
   * initEmptyEntitlementCheck - Shared pattern for checking empty entitlements at getEntitlement start
   */
  protected initEmptyEntitlementCheck(): void {
    if (!this.entitlementArray?.length) {
      this.isEmptyEntitlement = true;
    } else {
      this.isEmptyEntitlement = false;
      this.isShowEntitlement = true;
    }
  }

  /**
   * filterTravelPartyTickets - Filters entitlement array for travel party (non-guest) tickets
   */
  protected filterTravelPartyTickets(guestId: string): void {
    this.travelPartyTicketsArray = filter(
      this.entitlementArray,
      (item: any) => {
        if (this.getTicketGuestId(item) !== guestId) {
          return item;
        }
      },
    );
  }

  /**
   * partitionTravelMembers - Splits travel members into those with/without matching tickets
   * Uses getTicketGuestId to compare against travelPartyTicketsArray
   */
  protected partitionTravelMembers(): {
    equal: any[];
    notEqual: any[];
  } {
    const equal = filter(this.travelMembersDetails, (elem: any) => {
      return this.travelPartyTicketsArray.some(
        (ticket: any) => elem?.member_id === this.getTicketGuestId(ticket),
      );
    });
    const notEqual = filter(this.travelMembersDetails, (elem: any) => {
      return !this.travelPartyTicketsArray.some(
        (ticket: any) => elem?.member_id === this.getTicketGuestId(ticket),
      );
    });
    return { equal, notEqual };
  }

  /**
   * finalizeTravelParty - Common finalization for getTravelParty: set flags and ticketDetailArray
   */
  protected finalizeTravelParty(): void {
    this.showTabs = true;
    this.cdRef.markForCheck();
    this.ticketDetailArray = this.entitlementArray;
  }

  /**
   * commonUpdateTabActions - Shared actions at the end of updateTab
   */
  protected commonUpdateTabActions(): void {
    this.sortByDate();
    this.updateEmptyEntitlementState();
    this.sortEntitlements();
  }
}
